<template>
  <div
    :data-select-option-id="option.id"
    class="select-option"
    :class="{
      'open': Boolean(isOpen),
      'error': Boolean(error && !value),
      'selected': Boolean(value),
      'disabled': Boolean(disabledMessage),
    }"
  >
    <div class="select-option__main">
      <div
        class="select-option__select"
        @click="changeOpen"
      >
        <div
          v-if="valueImage"
          class="--image"
        >
          <nuxt-img
            :src="String(valueImage)"
            height="20"
            fit="fill"
            :alt="`select-option-image-${valueImage}`"
          />
        </div>
        <div class="--value">
          {{ valueLabel }}
          <span v-if="Boolean(helpAdminMessage)" v-html="helpAdminMessage" class="--admin-info"/>
        </div>
        <div
          v-if="priceLabel"
          class="--price"
          :data-price="priceLabel"
          :data-old-price="oldPriceLabel"
        >
          + {{ nuxtApp.$numberFormat(priceLabel) }} ₽
        </div>
        <div class="--icon">
          <img
            src="~/assets/img/svg/common/arrow-down.svg"
            alt="развернуть"
          />
        </div>
      </div>
      <template v-if="isOpen && !isOptionPresetGroup">
        <div
          class="select-option__dropdown"
          :class="{
            'loading': isLoadOptions,
            'scroll-customize-medium': $viewport.isGreaterOrEquals('md')
          }"
        >
          <template
            v-for="(variant, index) in variantsList"
            :key="`variant-${variant.id}`"
          >
            <ItemDropdown
              :variant="variant"
              :selected="selected"
              :customPrice="getVariantPrice(variant)"
              :is-user-admin="isUserAdmin"
              @change="changeVariant"
            />
            <div class="--divider" />
          </template>
        </div>
        <div
          class="select-option__close"
          @click="changeOpen"
        />
      </template>
    </div>
    <span v-if="Boolean(error && !value)" class="select-option__error">
      {{ error }}
    </span>
    <span v-if="Boolean(disabledMessage)" class="select-option__disabled-help">
      {{ disabledMessage }}
    </span>
    <template v-if="isOpen && isOptionPresetGroup">
      <DialogOptionPresetGroup
        :label="option?.name"
        :options="currentOption"
        :product="product"
        @close="closeFilter"
        @change="changeVariant"
      />
    </template>
  </div>
</template>

<script lang="ts" setup>
import { computed, type ComputedRef, ref } from 'vue';
import { useNuxtApp } from 'nuxt/app';
import useSitisUserManager from '~/composables/useSitisUserManager';
import { getVariantOldPriceRelationGroups, getVariantPriceRelationGroups } from '~/utils/productOptions';

const ItemDropdown = defineAsyncComponent(() => import('./_ItemDropdown.vue'));
const DialogOptionPresetGroup = defineAsyncComponent(() => import('./DialogOptionPresetGroup.vue'));

const props = defineProps({
  option: {
    type: Object as any,
    default: () => {}
  },
  selected: {
    type: Object as any,
    default: () => {}
  },
  product: {
    type: Object as any,
    default: () => {}
  },
  productSlug: {
    type: String,
    default: ''
  },
  value: {
    type: Object as any,
    default: () => {}
  },
  error: {
    type: String
  },

  disabledMessage: {
    type: String,
  }
});
const emit = defineEmits(['change']);
const nuxtApp = useNuxtApp();
const {
  userManagerInfo
} = useSitisUserManager();

const valueImage = computed(() => {
  if (isOpen.value) {
    return props.option.image_id
  }
  return props.value?.image_id || props.option.image_id;
});
const valueLabel = computed(() => {
  if (isOpen.value) {
    return props.option.name
  }
  return props.value?.name || props.option.name;
});
const valueSku = computed(() => {
  if (isOpen.value) {
    return ''
  }
  return props.value?.sku || '';
});
const priceLabel = computed(() => {
  if (!props.value || isOpen.value) {
    return ''
  }
  const groups = props?.value?.priceRelationGroups || [];
  if (groups.length > 0) {
    let basePrice = Number(props?.product?.price) || 0;
    groups.forEach((groupId: any) => {
      const key = String(groupId?.id || groupId);
      const selectedVariant = props.selected?.[key];
      if (selectedVariant) {
        basePrice += Number(selectedVariant.price || 0);
      }
    });
    const priceValue = Number(props.value?.price_value || 0);
    return String(Math.ceil((basePrice * priceValue) / 100));
  }
  return String(props.value?.price || 0);
});
const oldPriceLabel = computed(() => {
  if (!props.value || isOpen.value) {
    return ''
  }
  const groups = props?.value?.priceRelationGroups || [];
  if (groups.length > 0) {
    let basePrice = Number(props?.product?.oldPrice) || 0;
    groups.forEach((groupId: any) => {
      const key = String(groupId?.id || groupId);
      const selectedVariant = props.selected?.[key];
      if (selectedVariant) {
        basePrice += Number(selectedVariant.price_old || 0);
      }
    });
    const priceValue = Number(props.value?.price_old_value || 0);
    return String(Math.ceil((basePrice * priceValue) / 100));
  }
  return String(props.value?.price_old || 0);
});
const isOptionPresetGroup = computed(() => {
  return Boolean((currentOption.value?.variantGroups || []).length > 0);
});

const isOpen = ref(false);
const isOpenDialogOption = ref(false);
const changeOpen = () => {
  const newVal = !isOpen.value;
  isOpen.value = newVal;
  if (newVal) {
    (async () => {
      await getOptions();
    })();
  }
};
const closeFilter = () => {
  isOpen.value = false;
};

const isLoadOptions = ref(true);
const currentOption: any = ref(null);
const productOptions: any = ref([]);
const variantsList: any = computed(() => {
  return currentOption?.value?.variants || [];
});
const getOptions = async () => {
  if (!isLoadOptions.value || currentOption.value) {
    return;
  }

  const search = [
    'expand=options,options.variants,options.variants.characteristicVariants,options.variantGroups,options.optionPresetGroup.filterCharacteristics,options.variants.bonus_value'
  ].join('&');
  const res = await nuxtApp.$api.agent.get(`/products/${props.productSlug}?${search}`).then((res: any) => {
    return (res?._data?.options || []);
  }).catch(() => {
    return [];
  });
  productOptions.value = res || [];
  currentOption.value = res.find((t: any) => t?.id === props?.option?.id);
  isLoadOptions.value = false;
};

const changeVariant = (variant: any) => {
  emit('change', variant);
  isOpen.value = false;
};
const getSelectedOptionsForVariant = (variant: any) => {
  return (variant?.priceRelationGroups || []).reduce((acc: any, groupId: any) => {
    acc[String(groupId?.id || groupId)] = true;
    return acc;
  }, {});
};
const getVariantPrice = (variant: any) => {
  if ((variant?.priceRelationGroups || []).length <= 0) {
    return '';
  }
  const _selectedOptions = getSelectedOptionsForVariant(variant);
  return getVariantPriceRelationGroups(props?.product?.price, Number.parseFloat(variant?.price_value || 0), _selectedOptions);
};
const getVariantOldPrice = (variant: any) => {
  if ((variant?.priceRelationGroups || []).length <= 0) {
    return '';
  }
  const _selectedOptions = getSelectedOptionsForVariant(variant);
  return getVariantOldPriceRelationGroups(props?.product?.oldPrice, Number.parseFloat(variant?.price_old_value || 0), _selectedOptions);
};

const isUserAdmin: ComputedRef<boolean> = computed(() => {
  return Boolean(userManagerInfo?.value?.id || '');
});
const helpAdminMessage: ComputedRef<string> = computed(() => {
  if (!isUserAdmin.value) {
    return ''
  }
  const variantSku: string = props.value?.sku || '';
  const isPriceRelationGroups: boolean = Boolean((props.value?.priceRelationGroups || []).length > 0);
  return [
    Boolean(variantSku) && `(артикул: ${variantSku})`,
    Boolean(isPriceRelationGroups) && `(Зависимость)`
  ].filter((t: any) => !!t).join(', ');
});
</script>

<style lang="scss" scoped>
.select-option {
	position: relative;

	&:not(.selected) {
		.select-option__select {
			.--value {
				color: #989898;
			}
		}
	}

	&.open {
		z-index: 5;

		.select-option__select {
			z-index: 17;

			.--icon img {
				transform: rotate(180deg);
			}
		}

		.select-option__dropdown {
			z-index: 16;
		}
	}
	&.error {
		.select-option__select {
			border-color: #ED908C;
		}
		.select-option__select .--value {
			color: #ED908C;
		}
	}
  &.disabled {
    .select-option__select {
      opacity: 0.6;
      pointer-events: none;
    }
    &:hover {
      .select-option__disabled-help {
        display: block;
      }
    }
  }
}

.select-option__main {
	position: relative;
}

.select-option__select {
	display: flex;
	align-items: center;
	border-radius: 7px;
	background-color: white;
	border: 1px solid #989898;
	cursor: pointer;
	padding: 8px 16px;
	box-sizing: border-box;
	/** position: relative;
	z-index: 2; */

	.--image {
		position: relative;
		overflow: hidden;
		margin-right: 8px;
		height: 20px;

		img {
			object-fit: contain;
			position: relative;
			max-width: initial;
			max-height: initial;
		}
	}

	.--value {
		margin-right: auto;
		color: #000;
		font-variant-numeric: lining-nums proportional-nums;
		font-size: 12px;
		font-weight: 500;
		line-height: 20px;
	}

	.--admin-info {
		color: #ED908C;
		margin-left: 4px;
	}

	.--price {
		margin-right: 16px;
		color: #989898;
		font-variant-numeric: lining-nums proportional-nums;
		font-size: 12px;
		font-weight: 500;
		line-height: 20px;
		white-space: nowrap;
	}

	.--icon {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 20px;
		height: 20px;
		position: relative;

		img {
			width: 100%;
			height: 100%;
			object-fit: contain;
			position: absolute;
			top: 0;
			left: 0;
			filter: invert(69%) sepia(0%) saturate(0%) hue-rotate(121deg) brightness(88%) contrast(92%);
		}
	}

	&:hover {
		border-color: #404040;
	}
}

.select-option__error {
	color: #ED908C;
	font-size: 12px;
	font-weight: 500;
	line-height: 140%;
}

.select-option__dropdown {
	padding-top: 5px;
	box-sizing: border-box;
	position: absolute;
	top: 100%;
	left: 0;
	right: 0;
	transform: translateY(-5px);
	border-radius: 0 0 7px 7px;
	border: 1px solid #989898;
	border-top: 0;
	background-color: white;
	max-height: 240px;
	overflow: auto;

	&.loading {
		height: 40px;
		display: flex;
		align-items: center;
		padding-top: 4px;

		&::after {
			content: "Загружаем...";
			padding: 20px 16px;
			color: #989898;
			font-variant-numeric: lining-nums proportional-nums;
			font-size: 12px;
			font-weight: 500;
			line-height: 20px;
		}
	}

	.--divider {
		width: 100%;
		height: 1px;
		background-color: #EBEBEB;
	}
}

.select-option__close {
	position: fixed;
	z-index: 15;
	inset: 0;
}

.select-option-item {
	display: flex;
	align-items: center;
	padding: 12px 16px;
	cursor: pointer;

	.--image {
		position: relative;
		overflow: hidden;
		margin-right: 16px;
		height: 41px;
		max-width: 100px;

		img {
			object-fit: contain;
			position: relative;
			max-width: 100%;
			max-height: initial;
			mix-blend-mode: multiply;
		}
	}

	.--value {
		color: #000;
		font-variant-numeric: lining-nums proportional-nums;
		font-size: 12px;
		font-weight: 500;
		line-height: 20px;
	}

	.--price {
		margin-left: auto;
		color: #989898;
		font-variant-numeric: lining-nums proportional-nums;
		font-size: 12px;
		font-weight: 500;
		line-height: 20px;
		white-space: nowrap;
	}

	.--admin-info {
		color: #ED908C;
		font-variant-numeric: lining-nums proportional-nums;
		font-size: 12px;
		font-weight: 500;
		line-height: 20px;
		margin-left: 4px;
	}

	&:hover {
		background: #ebebeb59;

		.--image:after {
			border-color: rgb(248 248 248);
		}
	}
}

.select-option__disabled-help {
  display: none;
  z-index: 10;
  background: white;
  padding: 6px 12px;
  position: absolute;
  border-radius: 8px;
  pointer-events: none;
  border: 1px solid #989898;
  transform: translateX(-50%);
  left: 50%; top: calc(100% + 8px);

  color: #333333;
  font-size: 14px;
  font-weight: 600;
  line-height: 16px;
  text-align: center;

  &:after {
    content: "";
    z-index: -1;
    position: absolute;
    bottom: 100%; left: 50%;
    background: white;
    border: 1px solid #989898;
    width: 12px; height: 12px;
    transform: translateX(calc(-50% + 6px)) translateY(7px);
    rotate: 45deg;
    border-right: none;
    border-bottom: none;
  }
}

@media (max-width: 859px) {
  .select-option__close {
    display: none;
  }
  .select-option__dropdown {
    position: relative;
    max-height: 100%;
  }
	.select-option-item, .select-option__select {
    .--image {
      display: flex;
      align-items: center;
      justify-content: center;
      max-width: 64px;
      margin-right: 8px;

      img {
        max-width: 100%;
      }
    }

    &:hover {
      border-color: #989898;
    }
  }
}
</style>
